/**
 * The teacher area's root layout (ADR 0007): stamps `data-theme="teacher"`, boots the session
 * from the stored refresh token (ADR 0006) and puts the top bar above every screen.
 *
 * A student session here is sent to the feed and a teacher session on the student side is left
 * alone — build spec 5: "Teacher routes require a teacher session; a student session there
 * redirects to `/`." The login and setup screens sit outside the guard, since they are how a
 * session begins.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { API_URL, authedJson, authedVoid } from "../shared/api.js";
import type { TeacherMe } from "../shared/api-types.js";
import { endSession, getSession, refreshSession } from "../shared/auth-store.js";
import { germanMessage } from "../shared/errors.js";
import { storage } from "../shared/storage.js";
import { TButton } from "./ui.js";
import "./theme.css";

type Boot =
  | { status: "booting" }
  | { status: "anonymous" }
  | { status: "student" }
  | { status: "error"; message: string }
  | { status: "ready"; me: TeacherMe };

export function TeacherLayout() {
  const [boot, setBoot] = useState<Boot>({ status: "booting" });
  const [attempt, setAttempt] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const existing = getSession();
        const token =
          existing?.accessToken ?? (await refreshSession(API_URL, "teacher").catch(() => null));
        if (cancelled) return;
        if (!token) {
          setBoot({ status: "anonymous" });
          return;
        }
        if (getSession()?.subjectType === "student") {
          setBoot({ status: "student" });
          return;
        }
        const me = await authedJson<TeacherMe>("/teacher/me", { subjectType: "teacher" });
        if (!cancelled) setBoot({ status: "ready", me });
      } catch (err) {
        if (!cancelled) setBoot({ status: "error", message: germanMessage(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const signOut = useCallback(async () => {
    const refreshToken = await storage.get("refreshToken");
    if (refreshToken) {
      await authedVoid("/auth/logout", {
        method: "POST",
        body: JSON.stringify({ refreshToken }),
        subjectType: "teacher",
      }).catch(() => {
        // A dead session is still a sign-out: the local token goes either way.
      });
    }
    await endSession();
    void navigate("/lehrer/login", { replace: true });
  }, [navigate]);

  if (boot.status === "booting") {
    return (
      <Shell>
        <p role="status" className="px-4 py-16 text-center text-[var(--color-muted)]">
          Wird geladen…
        </p>
      </Shell>
    );
  }
  if (boot.status === "anonymous") {
    return <Navigate to="/lehrer/login" replace state={{ from: location.pathname }} />;
  }
  if (boot.status === "student") return <Navigate to="/" replace />;
  if (boot.status === "error") {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-4 px-4 py-16 text-center">
          <p className="m-0 text-[16px] text-[var(--color-ink)]">{boot.message}</p>
          <TButton
            variant="primary"
            onClick={() => {
              setBoot({ status: "booting" });
              setAttempt((value) => value + 1);
            }}
          >
            Nochmal versuchen
          </TButton>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <TopBar me={boot.me} onSignOut={() => void signOut()} />
      <main className="mx-auto w-full max-w-6xl px-4 py-6">
        <Outlet />
      </main>
    </Shell>
  );
}

/** The themed ground. Kept separate so the boot states are themed too, not bare white. */
export function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div data-theme="teacher" className="min-h-full bg-[var(--color-bg)] text-[var(--color-ink)]">
      {children}
    </div>
  );
}

function TopBar({ me, onSignOut }: { me: TeacherMe; onSignOut: () => void }) {
  return (
    <header className="no-print border-b border-[var(--color-line)] bg-[var(--color-surface)]">
      <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center gap-x-5 gap-y-2 px-4 py-3">
        <Link to="/lehrer" className="text-[18px] font-black text-[var(--color-ink)] no-underline">
          Scrolivo
        </Link>
        <span className="text-[15px] text-[var(--color-muted)]">{me.school.name}</span>
        <nav className="flex items-center gap-4">
          <NavLink to="/lehrer">Klassen</NavLink>
          <NavLink to="/lehrer/themen">Themen</NavLink>
          <NavLink to="/lehrer/meldungen">Meine Meldungen</NavLink>
        </nav>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-[14px] text-[var(--color-muted)]">{me.email}</span>
          <TButton onClick={onSignOut}>Abmelden</TButton>
        </div>
      </div>
    </header>
  );
}

function NavLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="text-[15px] font-semibold text-[var(--color-accent)] no-underline hover:underline"
    >
      {children}
    </Link>
  );
}
